import type { ReadonlyDefaultMap } from "@glideapps/ts-necessities";
import { DefaultMap, definedMap, hasOwnProperty } from "@glideapps/ts-necessities";
import { setUnionInto } from "collection-utils";
import {
    asBoolean,
    asMaybeNumber,
    asNumber,
    asObjectValue,
    asString,
    detectedRecursiveComputation,
    isLoadedDefined,
    isValueNotEmpty,
    loadedDefinedMap,
    RecomputingFromOneValueProducer,
    RecomputingValueProducer,
} from "../../components/component-helpers";
import type { BasePrimitiveValue, PrimitiveValue } from "../../components/computation-types";
import { Constant } from "../../components/data";
import { registerDeserializable } from "../../components/deserialization";
import { cleanNumber } from "../../components/functions";
import type { Icon } from "../../components/icons";
import { areValuesEqual } from "../../components/primitives";
import type {
    AppDescription,
    AppKind,
    SerializedComponent,
    SerializedComponentsObject,
    UserFeatures,
} from "../../components/SerializedApp";
import { defaultAppKind, getAppKind, getSourceMetadata, isNativeTable } from "../../components/SerializedApp";
import type {
    AppEnvironment,
    AppFacilities,
    MinimalAppEnvironment,
    QuotaBannerRequirements,
    UserProfileTableInfo,
    ValueProducer,
} from "../../components/types";
import { getUserProfileTableInfo } from "../../components/user-profile";
import type { GroundValue, Row } from "../../computation-model/data";
import type { RelativePath, RootPath } from "../../computation-model/paths";
import type { Owner, UserData } from "../../Database";
import type { TableName } from "../../database-strings";
import type { BuilderAction, BuilderActionsForApp } from "../../Database/builder-actions";
import type { EminenceFlags } from "../../Database/eminence";
import { logError, logInfo } from "../../debug-print";
import { getDocURL } from "../../docUrl";
import { addUSD, formatNumberFixed, makeFixedNumberFormatter } from "../../format-number";
import { memoizeFunction } from "../../memoize";
import type { Sound } from "../../sound";
import { defined, definedMapWithDefault, isArray, mapRecord, panic } from "../../Support";
import type { WireComponent, WireScreen } from "../../wire";
import type { WireAction, WireEditableValue } from "../../wire/types";
import type { CompoundActionNode } from "../actions/compound";
import type {
    ActionComponentDescription,
    ActionDescription,
    ActionKind,
    AppUpdater,
    ArrayItemType,
    ArrayTransform,
    ClassOrArrayScreenDescription,
    ClassScreenDescription,
    ColumnAssignment,
    ColumnType,
    ColumnTypeKind,
    ComponentDescription,
    ComponentKind,
    Description,
    FilterArrayTransform,
    FormScreenDescription,
    Formula,
    InputOutputTables,
    LegacyPropertyDescription,
    MutatingScreenKind,
    PropertyDescription,
    PropertySource,
    SourceColumn,
    TableAndColumn,
    TableColumn,
    TableGlideType,
    TableRefGlideType,
    TypeSchema,
} from "../description";
import { findTable, getPrimitiveColumns, PropertyKind, sheetNameForTable } from "../description";
import { fetchLinkColumn, SerializedElementBuilder } from "../serialized-component";
import type { PageScreenPosition, ParsedPath, ParsedScreen } from "./parsed-path";
import type { PropertySection, SuperPropertySection } from "./property-sections";
import { getActionProperty, makeActionProperty } from "./property-utils";

// title, subtitle, image, link
export type Summary = [
    SerializedComponent | undefined,
    SerializedComponent | undefined,
    SerializedComponent | undefined,
    SerializedComponent | undefined
];

export interface AllowedDataEdits {
    readonly addRowToTable: ReadonlyDefaultMap<TableGlideType, ReadonlySet<string>>;
    readonly setColumnsInRow: ReadonlyDefaultMap<TableGlideType, ReadonlySet<string>>;
    readonly deleteRow: ReadonlySet<TableGlideType>;
}

export interface MutableAllowedDataEdits extends AllowedDataEdits {
    readonly addRowToTable: DefaultMap<TableGlideType, Set<string>>;
    readonly setColumnsInRow: DefaultMap<TableGlideType, Set<string>>;
    readonly deleteRow: Set<TableGlideType>;
}

export function makeEmptyMutableAllowedDataEdits(): MutableAllowedDataEdits {
    return {
        addRowToTable: new DefaultMap(() => new Set()),
        setColumnsInRow: new DefaultMap(() => new Set()),
        deleteRow: new Set(),
    };
}

export function mergeAllowedDataEdits(dst: MutableAllowedDataEdits, src: AllowedDataEdits | undefined): void {
    if (src === undefined) return;

    for (const [k, v] of src.addRowToTable) {
        setUnionInto(dst.addRowToTable.get(k), v);
    }
    for (const [k, v] of src.setColumnsInRow) {
        setUnionInto(dst.setColumnsInRow.get(k), v);
    }
    setUnionInto(dst.deleteRow, src.deleteRow);
}

export function convertEditedColumnsToAllowedDataEdits(
    editedColumns: EditedColumns,
    schema: SchemaInspector
): MutableAllowedDataEdits {
    const edits = makeEmptyMutableAllowedDataEdits();
    for (const [cn, , isAddRow, tn] of editedColumns) {
        const t = schema.findTable(tn);
        if (t === undefined) continue;

        if (isAddRow) {
            edits.addRowToTable.get(t).add(cn);
        } else {
            edits.setColumnsInRow.get(t).add(cn);
        }
    }
    return edits;
}

export function convertEditedColumnsToIndirect(editedColumns: EditedColumns): EditedColumns {
    return editedColumns.map(([cn, , isAddRow, tn]) => [cn, false, isAddRow, tn]);
}

export interface UserProfileRowBuild {
    readonly row: SerializedComponent;
    readonly table: TableGlideType;
}

export interface ValueForColumn {
    readonly value: SerializedComponent;
    readonly table: TableGlideType | undefined;
    readonly column: TableColumn | undefined;
}

export interface FetchingBuilder extends SchemaInspector {
    readonly b: SerializedElementBuilder;
    readonly isBuilder: boolean;

    info(...args: readonly any[]): void;
    error(...args: readonly any[]): void;
    fetchTableRows(tableName: TableName, forDataEditor: boolean): SerializedComponent;
    fetchLinkColumn(column: TableColumn, columnCtx: SerializedComponent, forDataEditor: boolean): SerializedComponent;
    registerTableUsed(tableName: TableName): void;

    makeUserProfileRow(): UserProfileRowBuild | undefined;

    // We will never try to compile to JS if this always return `false`
    getCompiledJSCode(name: string): SerializedComponent | undefined | false;
    setCompiledJSFailed?(name: string): void;
    setCompiledJavaScript?(name: string, code: string, serializedComponent: SerializedComponent): void;
}

export const builderJSCompiledFormulas = new Map<string, SerializedComponent | false>();

export const compiledJSCallbacks = {
    asObjectValue,
    definedMap,
    isValueNotEmpty,
    isLoadedDefined,
    loadedDefinedMap,
    RecomputingValueProducer,
    RecomputingFromOneValueProducer,
    asBoolean,
    asString,
    asNumber,
    areValuesEqual,
    registerDeserializable,
    asMaybeNumber,
    cleanNumber,
    formatNumberFixed,
    Constant,
    makeFixedNumberFormatter,
    addUSD,
    detectedRecursiveComputation,
};

export type CompiledJSCallbacks = Record<keyof typeof compiledJSCallbacks, unknown>;

export class SimpleFetchingBuilder implements FetchingBuilder {
    public readonly b: SerializedElementBuilder = new SerializedElementBuilder();

    constructor(
        public readonly schema: TypeSchema,
        public readonly userProfileTableInfo: UserProfileTableInfo | undefined,
        public readonly isBuilder: boolean
    ) {}

    public findTable(ref: TableName | TableRefGlideType | undefined): TableGlideType | undefined {
        return findTable(this.schema, ref);
    }

    // FIXME: Why do we take `forBuilder` here when we have it?
    public fetchTableRows(tableName: TableName, forBuilder: boolean): SerializedComponent {
        return this.b.fetchTableRows("default", tableName.name, forBuilder);
    }

    // FIXME: Why do we take `forBuilder` here when we have it?
    public fetchLinkColumn(
        column: TableColumn,
        columnCtx: SerializedComponent,
        forBuilder: boolean
    ): SerializedComponent {
        return fetchLinkColumn(this.b, column, columnCtx, forBuilder);
    }

    public registerTableUsed(_tableName: TableName): void {
        return;
    }

    public info(...args: readonly any[]): void {
        logInfo(...args);
    }

    public error(...args: readonly any[]): void {
        logError(...args);
    }

    public makeUserProfileRow(): UserProfileRowBuild | undefined {
        const tableName = this.userProfileTableInfo?.tableName;
        if (tableName === undefined) return undefined;

        const table = this.findTable(tableName);
        if (table === undefined) return undefined;

        return {
            row: this.b.userProfileRow,
            table,
        };
    }

    public getCompiledJSCode(_name: string): SerializedComponent | undefined | false {
        return false;
    }
}

export class SimpleFetchingEvaluatingBuilder extends SimpleFetchingBuilder {
    public getCompiledJSCode(name: string): SerializedComponent | undefined | false {
        return builderJSCompiledFormulas.get(name);
    }

    public setCompiledJSFailed(name: string): void {
        builderJSCompiledFormulas.set(name, false);
    }

    public setCompiledJavaScript(name: string, code: string, serializedComponent: SerializedComponent): void {
        builderJSCompiledFormulas.set(name, serializedComponent);
        // eslint-disable-next-line no-eval
        eval(code)(compiledJSCallbacks);
    }
}

export interface ComponentBuilder extends FetchingBuilder, AppDescriptionContext {
    makeComponents(
        uniqueIDPrefix: string,
        componentDescriptions: readonly ComponentDescription[],
        tables: InputOutputTables,
        mutatingScreenKind: MutatingScreenKind | undefined,
        // This must not depend on the context, i.e. should be a `getVariable`
        outputContext: SerializedComponent,
        searchVariable: SerializedComponent | undefined,
        searchNotActive: SerializedComponent | undefined,
        visibilityConditions: readonly (SerializedComponent | undefined)[],
        validationVariables: readonly (SerializedComponent | undefined)[]
    ): { components: readonly SerializedComponent[]; quotaRequirements: QuotaBannerRequirements };
    makeVisibilityConditions(
        table: TableGlideType,
        components: readonly ComponentDescription[],
        context: SerializedComponent
    ): readonly (SerializedComponent | undefined)[];
    makeColumnValues(
        tables: InputOutputTables,
        assignments: readonly ColumnAssignment[],
        alreadyAssigned: ReadonlySet<string> | undefined,
        defaultValues: readonly [string, SerializedComponent][],
        screenContext: SerializedComponent | undefined,
        undefinedIfEmpty: boolean
    ): SerializedComponentsObject;
    makePushScreen(
        screenNameOrType: string | ColumnType,
        title: SerializedComponent | undefined,
        itemID: SerializedComponent | undefined,
        screenData: SerializedComponent
    ): SerializedComponent | undefined;
    makePushAddItemScreen(itemType: ArrayItemType, arrayData: SerializedComponent): SerializedComponent | undefined;
    makePushEditItemScreen(
        itemType: TableRefGlideType,
        itemData: SerializedComponent,
        title: SerializedComponent | undefined
    ): SerializedComponent | undefined;
    makePushFormScreen(
        screenName: string,
        title: SerializedComponent,
        screenContext: SerializedComponent
    ): SerializedComponent | undefined;
    makePushFreeScreen(
        screenName: string,
        title: string | SerializedComponent | undefined
    ): SerializedComponent | undefined;
    makePushMenuScreen(
        menuID: string,
        primaryKey: SerializedComponent,
        defaultScreenName: string,
        title: SerializedComponent | undefined,
        screenContext: SerializedComponent
    ): SerializedComponent | undefined;
    makeSequenceFromActions(
        descs: ReadonlyArray<ActionDescription>,
        tables: InputOutputTables,
        screenContext: SerializedComponent | undefined,
        outputContext: SerializedComponent,
        mutatingScreenKind: MutatingScreenKind | undefined,
        fromArrayScreen: boolean
    ): SerializedComponent | undefined;
    makePushFilterScreen(
        items: SerializedComponent | undefined,
        values: SerializedComponent | undefined,
        filterColumn: SerializedComponent | undefined,
        sortProperties: SerializedComponent | undefined,
        sortProperty: SerializedComponent | undefined,
        sortAscending: SerializedComponent | undefined
    ): SerializedComponent;
    makePushOverlayEditorScreen(
        value: SerializedComponent,
        title: SerializedComponent,
        placeholder: SerializedComponent,
        doneText: SerializedComponent,
        doneIcon: SerializedComponent,
        doneStyle: SerializedComponent,
        allowEmpty: boolean,
        onChange: SerializedComponent
    ): SerializedComponent;
    makePushChoiceScreen(
        value: SerializedComponent,
        choices: SerializedComponent,
        title: SerializedComponent | undefined,
        isRequired: SerializedComponent,
        onChange: SerializedComponent | undefined
    ): SerializedComponent;
    makePushSignaturePadScreen(
        message: SerializedComponent,
        signatureURL: SerializedComponent,
        onChange: SerializedComponent | undefined,
        validFlag: SerializedComponent | undefined
    ): SerializedComponent;
    makePushCodeScannerScreen(
        standards: SerializedComponent,
        acceptOnFirst: SerializedComponent,
        output: SerializedComponent,
        onSuccess: SerializedComponent | undefined
    ): SerializedComponent;
    makePushLiveLinearCodeScannerScreen(
        output: SerializedComponent,
        onSuccess: SerializedComponent | undefined
    ): SerializedComponent;
    makePushEventPickerScreen(
        startValue: SerializedComponent,
        endValue: SerializedComponent,
        events: SerializedComponent | undefined,
        title: SerializedComponent,
        isRequired: SerializedComponent,
        onChange: SerializedComponent | undefined
    ): SerializedComponent;
    makePushSendAppFeedbackScreen(): SerializedComponent;
    classScreenDescriptionForTable(t: TableRefGlideType): ClassScreenDescription | undefined;
}

export function updatesForUntypedProperty<T extends Description>(
    source: PropertySource<T>,
    value: unknown | undefined
): Partial<T> {
    if (hasOwnProperty(source, "name")) {
        const updates: { [p: string]: PropertyDescription | undefined } = {};
        updates[source.name] = value as any;
        return updates as any;
    } else {
        return panic("Untyped updates not supported for indirect setters");
    }
}

export function updatesForProperty<T extends Description>(
    source: PropertySource<T>,
    desc: T,
    value: PropertyDescription | undefined,
    updater: AppUpdater | undefined
): Partial<T> {
    if (hasOwnProperty(source, "name")) {
        return updatesForUntypedProperty(source, value);
    } else {
        return source.update(desc, value, updater);
    }
}

export function updatesForPropertyValue<P, T extends Description>(
    kind: PropertyKind,
    source: PropertySource<T>,
    desc: T,
    value: P | undefined,
    updater: AppUpdater | undefined
): Partial<T> {
    return updatesForProperty(
        source,
        desc,
        definedMap(value, v => ({ kind, value: v })),
        updater
    );
}

export function isNamedPropertySource<T extends Description>(
    source: PropertySource<T>
): source is { readonly name: string } {
    return hasOwnProperty(source, "name");
}

export function getPropertyDescription<P, T extends Description>(
    desc: T,
    descr: PropertyDescriptor
): PropertyDescription | undefined {
    const source = descr.property;
    let value: LegacyPropertyDescription | undefined;
    if (isNamedPropertySource(source)) {
        value = (desc as any)[source.name] ?? source.defaultValue;
    } else {
        value = source.get(desc);
    }
    if (value === undefined) return undefined;

    const pdc = isMultiCasePropertyDescriptor(descr) ? defined(descr.cases[0]) : descr;
    // FIXME: Move this into description handler
    if (pdc.kind === PropertyKind.Formula || pdc.kind === PropertyKind.Transforms || pdc.kind === PropertyKind.Sorts) {
        // `P` kinda has to be `Formula` here.
        return { kind: pdc.kind, value: value as P };
    }
    if (typeof value === "object" && hasOwnProperty(value, "kind")) {
        return value as PropertyDescription;
    }
    if (pdc.kind === PropertyKind.Filter) {
        if (!hasOwnProperty(value, "operator")) {
            return panic("Filter property description is invalid");
        }
    } else if (pdc.kind === PropertyKind.Action) {
        if (isArray(value) && value.length === 0) return undefined;
        value = getActionProperty(value);
        if (value === undefined) {
            logError("Action property description is invalid", value);
            return undefined;
        }
    } else if (typeof value !== "string" && typeof value !== "boolean") {
        logError("Property description is neither string nor boolean", value);
        return undefined;
    }
    return { kind: pdc.kind, value };
}

export function getPropertyDescriptionValue<P, T extends Description>(
    desc: T,
    descr: PropertyDescriptor
): P | undefined;
export function getPropertyDescriptionValue<P, T extends Description, U>(
    desc: T,
    descr: PropertyDescriptor,
    f: (v: LegacyPropertyDescription) => U | undefined
): U | undefined;
export function getPropertyDescriptionValue<P, T extends Description, U>(
    desc: T,
    descr: PropertyDescriptor,
    f?: (v: LegacyPropertyDescription) => U | undefined
): P | U | undefined {
    const pd = getPropertyDescription<P, T>(desc, descr);
    if (pd === undefined) return undefined;
    if (f === undefined) return pd.value as P;
    return f(pd);
}

export type PropertyLabel = string | ((i: number | undefined) => string);

export interface Subcomponent {
    readonly name: string;
    readonly important: boolean;
    readonly order?: number; // defaults to 0
}

interface PropertyDescriptorBase<T extends Description> {
    readonly property: PropertySource<T>;
    readonly label: PropertyLabel;
    readonly accessoryTo?: PropertyDescriptorBase<any>;
    readonly section: SuperPropertySection;
    readonly subcomponent?: Subcomponent;
    readonly writesToColumn?: TableAndColumn;
    // All configurators will be sorted within their section.  The ones with a
    // position come first, sorted by the position.  After that come all the
    // rest.
    readonly position?: number;
    readonly isSearchable?: boolean; // defaults to `true`
}

interface ConfigurablePropertyDescriptor<T extends Description> extends PropertyDescriptorBase<T> {
    // This must only be set to `true` if the property value doesn't actually
    // reside in the corresponding description, and will be rewritten at its
    // actual location, such as the source sheet for the Show New Screen action.
    readonly doNotRewrite?: boolean;
}

export type ColumnTypePredicate = ((t: ColumnType) => boolean) | ColumnTypeKind;

export type GetAllowedColumnsFunction<T extends Description> = (
    table: TableGlideType,
    desc: T,
    schema: SchemaInspector
) => readonly TableColumn[];

interface IndirectTableGetter<T extends Description> {
    getIndirectTable?(tables: InputOutputTables, desc: T, schema: SchemaInspector): TableGlideType | undefined;
}

export interface ColumnPropertyDescriptorCase<T extends Description> extends IndirectTableGetter<T> {
    readonly kind: PropertyKind.Column;

    readonly required: boolean;
    readonly editable: boolean;
    readonly searchable: boolean;
    readonly emptyByDefault?: boolean; // defaults to `false`
    readonly isDefaultCaption?: boolean;
    readonly isLegacy?: boolean;
    readonly isEditedInApp?: boolean;
    readonly applyFormat?: boolean;
    // The value must be already present, i.e. binding this to an output
    // column without a pre-existing input is invalid.  We use this for
    // the default value properties.
    readonly needsExistingValue?: boolean;
    readonly allowUserProfileColumns?: boolean;
    getAllowedColumns: GetAllowedColumnsFunction<T>;
    readonly preferredNames?: ReadonlyArray<string>;
    // This one is currently only used for the caption property, to discourage
    // Glide picking the same property as it picked for title or description.
    // I think this should go away, however, and we should by default not pick
    // a column that's used for another property.
    readonly ignoreColumnsFromProperties?: readonly string[];
    readonly preferredType?: ColumnTypePredicate;
    getEmptyCase?(tables: InputOutputTables): { label: string; linkURL: string };
}

export interface ColumnPropertyDescriptor<T extends Description>
    extends ConfigurablePropertyDescriptor<T>,
        ColumnPropertyDescriptorCase<T> {}

export interface TablePropertyDescriptorCase<T extends Description> {
    readonly kind: PropertyKind.Table;

    readonly required: boolean;
    readonly isDefaultCaption?: boolean;
    getAllowedTables(
        desc: T,
        schema: TypeSchema,
        componentTables: InputOutputTables | undefined
    ): ReadonlyArray<TableGlideType>;
}

export interface TablePropertyDescriptor<T extends Description>
    extends ConfigurablePropertyDescriptor<T>,
        TablePropertyDescriptorCase<T> {}

export interface EnumPropertyCase<P extends BasePrimitiveValue> {
    readonly value: P;
    readonly label: string;
    readonly icon?: string;

    // This is currently used by the Favorites overlay button to indicate that
    // it's writing to the "Is Favorited?" column.
    readonly columnWritten?: string;
}

export type EnumVisual = "dropdown" | "small-images" | "large-images" | "slider" | "radio";

// `getIndirectTable` is only used if some of the cases have a `columnWritten`.
export interface EnumPropertyDescriptorCase<P extends BasePrimitiveValue, T extends Description>
    extends IndirectTableGetter<T> {
    readonly kind: PropertyKind.Enum;

    readonly menuLabel: string;
    readonly cases: readonly EnumPropertyCase<P>[];
    // If this is specified, the configurator will work even if
    // the value is `undefined`.
    readonly defaultCaseValue?: P;
    readonly visual: EnumVisual;
    // Should be `true` if the value of this enum influences anything
    // else about the component descriptor.  The list layout in the
    // Inline List is an example.
    readonly changesDescriptor?: boolean;
}

export interface EnumPropertyDescriptor<P extends BasePrimitiveValue, T extends Description>
    extends ConfigurablePropertyDescriptor<T>,
        EnumPropertyDescriptorCase<P, T> {}

export interface StringPropertyDescriptorCase {
    readonly kind: PropertyKind.String;

    readonly required: boolean;
    readonly placeholder: string;
    readonly isImageURL?: boolean;
    // `undefined` means it's not the caption.  A string means
    // it's the caption, and that string is the default caption.
    readonly isCaption?: string;
    readonly isMultiLine?: boolean;
    readonly menuLabel?: string;
    readonly defaultValue?: string;
    readonly syntaxMode?: string;
}

export interface StringPropertyDescriptor<T extends Description>
    extends ConfigurablePropertyDescriptor<T>,
        StringPropertyDescriptorCase {}

export enum NumberPropertyStyle {
    Entry,
    Stepper,
}

export enum RequiredKind {
    Required,
    NotRequiredDefaultPresent,
    NotRequiredDefaultMissing,
}

export interface NumberPropertyDescriptorCase {
    readonly kind: PropertyKind.Number;

    readonly required: RequiredKind;
    readonly placeholder: string;
    readonly style?: NumberPropertyStyle;
    readonly defaultValue: number;
}

export interface NumberPropertyDescriptor<T extends Description>
    extends ConfigurablePropertyDescriptor<T>,
        NumberPropertyDescriptorCase {}

export interface SwitchPropertyDescriptorCase {
    readonly kind: PropertyKind.Switch;

    readonly defaultValue?: boolean;
}

export interface SwitchPropertyDescriptor<T extends Description>
    extends ConfigurablePropertyDescriptor<T>,
        SwitchPropertyDescriptorCase {}

export interface ConstantPropertyDescriptorCase {
    readonly kind: PropertyKind.Constant;

    readonly value: string;
}

interface ConstantPropertyDescriptor<T extends Description>
    extends PropertyDescriptorBase<T>,
        ConstantPropertyDescriptorCase {
    readonly doNotRewrite: true;
}

export interface SpecialValuePropertyDescriptorCase {
    readonly kind: PropertyKind.SpecialValue;
    readonly withActionSource: boolean;
    readonly withClearColumn: boolean;
}

interface SpecialValuePropertyDescriptor<T extends Description>
    extends ConfigurablePropertyDescriptor<T>,
        SpecialValuePropertyDescriptorCase {}

export interface PaymentMethodPropertyDescriptorCase {
    readonly kind: PropertyKind.PaymentMethod;
}

interface PaymentMethodPropertyDescriptor<T extends Description>
    extends ConfigurablePropertyDescriptor<T>,
        PaymentMethodPropertyDescriptorCase {}

export interface ScreenPropertyDescriptorCase {
    readonly kind: PropertyKind.Screen;
}

interface ScreenPropertyDescriptor<T extends Description>
    extends ConfigurablePropertyDescriptor<T>,
        ScreenPropertyDescriptorCase {}

export interface FilterPropertyDescriptorCase<T extends Description> extends IndirectTableGetter<T> {
    readonly kind: PropertyKind.Filter;
}

export function getFilterTable<T extends Description>(
    descr: FilterPropertyDescriptorCase<T>,
    tables: InputOutputTables,
    desc: T,
    schema: SchemaInspector
): TableGlideType | undefined {
    return definedMapWithDefault(descr.getIndirectTable, tables.input, f => f(tables, desc, schema));
}

interface FilterPropertyDescriptor<T extends Description>
    extends ConfigurablePropertyDescriptor<T>,
        FilterPropertyDescriptorCase<T> {}

enum FormulaTemplate {
    FilterReference = "filter-reference",
    TextTemplate = "text-template",
    Predicate = "predicate",
}

export interface FormulaPropertyDescriptorCase {
    readonly kind: PropertyKind.Formula;

    readonly template: FormulaTemplate;
}

interface FormulaPropertyDescriptor<T extends Description>
    extends ConfigurablePropertyDescriptor<T>,
        FormulaPropertyDescriptorCase {}

export interface TransformsPropertyDescriptorCase<T extends Description> extends IndirectTableGetter<T> {
    readonly kind: PropertyKind.Transforms;

    addText: string;
    description: string;
    allowContextTable: boolean;
    allowLHSUserProfileColumns: boolean;
    specPrefix?: string;
    withContainingScreen: boolean;
}

interface TransformsPropertyDescriptor<T extends Description>
    extends ConfigurablePropertyDescriptor<T>,
        TransformsPropertyDescriptorCase<T> {}

export interface SortsPropertyDescriptorCase<T extends Description> extends IndirectTableGetter<T> {
    readonly kind: PropertyKind.Sorts;
}

interface SortsPropertyDescriptor<T extends Description>
    extends ConfigurablePropertyDescriptor<T>,
        SortsPropertyDescriptorCase<T> {}

export enum ArrayPropertyStyle {
    Default,
    // For these two, `properties` must have two items: first the
    // key/caption/title, then the value.
    KeyValue,
    ActionArray,
    Card,
}

export interface ArrayPropertyDescriptorCase {
    readonly kind: PropertyKind.Array;

    readonly properties: readonly PropertyDescriptor[];
    readonly allowEmpty: boolean;
    readonly addItemLabel: string;
    readonly maxItems?: number;
    readonly specialItems?: number;
    readonly defaultValue?: boolean | readonly Description[];
    readonly allowReorder: boolean;
    readonly style: ArrayPropertyStyle;
}

interface ArrayPropertyDescriptor<T extends Description>
    extends ConfigurablePropertyDescriptor<T>,
        ArrayPropertyDescriptorCase {}

export interface ZapPropertyDescriptorCase {
    readonly kind: PropertyKind.Zap;
}

export interface ZapPropertyDescriptor<T extends Description>
    extends ConfigurablePropertyDescriptor<T>,
        ZapPropertyDescriptorCase {}

export interface WebhookPropertyDescriptorCase {
    readonly kind: PropertyKind.Webhook;
}

export interface WebhookPropertyDescriptor<T extends Description>
    extends ConfigurablePropertyDescriptor<T>,
        WebhookPropertyDescriptorCase {}

export interface IconPropertyDescriptorCase {
    readonly kind: PropertyKind.Icon;

    readonly defaultIcon?: Icon;
}

export interface IconPropertyDescriptor<T extends Description>
    extends ConfigurablePropertyDescriptor<T>,
        IconPropertyDescriptorCase {}

export interface EmojiPropertyDescriptorCase {
    readonly kind: PropertyKind.Emoji;

    readonly defaultEmoji?: string;
}

export interface EmojiPropertyDescriptor<T extends Description>
    extends ConfigurablePropertyDescriptor<T>,
        EmojiPropertyDescriptorCase {}

// We're extending `IndirectTableGetter` here which seems to duplicate
// ##getActionTables.
export interface ActionPropertyDescriptorCase<T extends Description> extends IndirectTableGetter<T> {
    readonly kind: PropertyKind.Action;

    readonly kinds: readonly ActionKind[];
    readonly defaultAction?: boolean | ((table: TableGlideType, desc: T) => ActionDescription | undefined);
    // This is only used for ##inlineListDefaultAction.
    readonly defaultActionForUndefined?: boolean;
    readonly required?: boolean;
}

export interface ActionPropertyDescriptor<T extends Description>
    extends ConfigurablePropertyDescriptor<T>,
        ActionPropertyDescriptorCase<T> {}

export interface CompoundActionPropertyDescriptorCase {
    readonly kind: PropertyKind.CompoundAction;
}

interface CompoundActionPropertyDescriptor<T extends Description>
    extends ConfigurablePropertyDescriptor<T>,
        CompoundActionPropertyDescriptorCase {}

export function getTransformsTable<T extends Description>(
    descr: TransformsPropertyDescriptorCase<T> | SortsPropertyDescriptorCase<T>,
    tables: InputOutputTables,
    desc: T,
    schema: SchemaInspector
): TableGlideType | undefined {
    return definedMapWithDefault(descr.getIndirectTable, tables.input, f => f(tables, desc, schema));
}

export function shouldUseInputContextForTransform(mutatingScreenKind: MutatingScreenKind | undefined): boolean {
    // In a regular detail screen, the containing screen context is
    // the screen's context.  In mutating screens we have the input
    // and the output context.  We pick the output context as the
    // containing screen context.  Ideally we'd support both.
    return mutatingScreenKind === undefined;
}

export type PropertyDescriptorCase<T extends Description> =
    | ColumnPropertyDescriptorCase<T>
    | TablePropertyDescriptorCase<T>
    | EnumPropertyDescriptorCase<BasePrimitiveValue, T>
    | StringPropertyDescriptorCase
    | NumberPropertyDescriptorCase
    | SwitchPropertyDescriptorCase
    | ConstantPropertyDescriptorCase
    | SpecialValuePropertyDescriptorCase
    | PaymentMethodPropertyDescriptorCase
    | ScreenPropertyDescriptorCase
    | FilterPropertyDescriptorCase<T>
    | FormulaPropertyDescriptorCase
    | TransformsPropertyDescriptorCase<T>
    | SortsPropertyDescriptorCase<T>
    | ArrayPropertyDescriptorCase
    | ZapPropertyDescriptorCase
    | WebhookPropertyDescriptorCase
    | IconPropertyDescriptorCase
    | EmojiPropertyDescriptorCase
    | ActionPropertyDescriptorCase<T>
    | CompoundActionPropertyDescriptorCase;

export interface MultiCasePropertyDescriptor<T extends Description> extends ConfigurablePropertyDescriptor<T> {
    readonly cases: ReadonlyArray<PropertyDescriptorCase<T>>;
}

export function isMultiCasePropertyDescriptor(descr: PropertyDescriptor): descr is MultiCasePropertyDescriptor<any> {
    return (descr as any).kind === undefined;
}

export type PropertyDescriptor =
    | ColumnPropertyDescriptor<any>
    | TablePropertyDescriptor<any>
    | EnumPropertyDescriptor<any, any>
    | StringPropertyDescriptor<any>
    | NumberPropertyDescriptor<any>
    | SwitchPropertyDescriptor<any>
    | ConstantPropertyDescriptor<any>
    | MultiCasePropertyDescriptor<any>
    | SpecialValuePropertyDescriptor<any>
    | PaymentMethodPropertyDescriptor<any>
    | ScreenPropertyDescriptor<any>
    | FilterPropertyDescriptor<any>
    | FormulaPropertyDescriptor<any>
    | TransformsPropertyDescriptor<any>
    | SortsPropertyDescriptor<any>
    | ArrayPropertyDescriptor<any>
    | ZapPropertyDescriptor<any>
    | WebhookPropertyDescriptor<any>
    | IconPropertyDescriptor<any>
    | EmojiPropertyDescriptor<any>
    | ActionPropertyDescriptor<any>
    | CompoundActionPropertyDescriptor<any>;

export type PropertyTableGetter<T> = (
    componentTables: InputOutputTables,
    desc: T,
    schema: SchemaInspector
) => TableGlideType | undefined;

// "optional" is ignored.  It's only here to do `flag ? "required" : "optional"`
export type StringyColumnPropertyFlag =
    | "required"
    | "optional"
    | "editable"
    | "searchable"
    | "edited-in-app"
    | "empty-by-default";

export function makeStringyColumnPropertyDescriptor<T extends Description>(
    name: string,
    label: string,
    flags: readonly StringyColumnPropertyFlag[],
    section: PropertySection,
    getPropertyTable: PropertyTableGetter<T> | undefined,
    preferredNames: ReadonlyArray<string> | undefined,
    ignoreColumnsFromProperties: readonly string[] = [],
    preferredType: ColumnTypePredicate = "string"
): PropertyDescriptor {
    const required = flags.indexOf("required") >= 0;
    const editable = flags.indexOf("editable") >= 0;
    const searchable = flags.indexOf("searchable") >= 0;
    const isEditedInApp = flags.indexOf("edited-in-app") >= 0;
    const emptyByDefault = flags.indexOf("empty-by-default") >= 0;
    return {
        kind: PropertyKind.Column,
        property: { name },
        label,
        required,
        editable,
        searchable,
        isEditedInApp,
        emptyByDefault,
        getIndirectTable: getPropertyTable,
        getAllowedColumns: getPrimitiveColumns,
        preferredNames,
        ignoreColumnsFromProperties,
        preferredType,
        section,
    };
}

// FIXME: This and `updateForActionDescriptions` have to go away.  We shouldn't
// have a pre-defined `actions` property anymore.  All actions should just be
// regular properties.
const actionsPropertyName = "actions";

export function updateForActionDescriptions<T extends Description>(
    actions: ReadonlyArray<ActionDescription>
): Partial<T> {
    const updates: Partial<T> = {};
    (updates as any)[actionsPropertyName] = actions;
    return updates;
}

// All actions other than the ones in `actions` are regular properties.
export function wrapActionProperties(actions: ActionsRecord): Partial<ActionComponentDescription> {
    return mapRecord(actions, (v, k) => {
        if (k === actionsPropertyName) return v;
        if (v.length === 0) return undefined;
        return makeActionProperty(v[0]);
    });
}

export interface ComponentDescriptor<T extends Description> {
    readonly name: string;
    readonly description: string;
    readonly img: string;
    readonly group: string;
    readonly isLegacy?: boolean;
    readonly isHidden?: boolean;
    readonly isPro?: boolean;
    readonly needsInputContext?: boolean; // default is `false`
    readonly properties: ReadonlyArray<PropertyDescriptor>;
    readonly helpUrl: string;
    readonly specialCaseDescriptor?: ComponentSpecialCaseDescriptor;
    readonly quotaWarning?: string;
    // Does this component let the user also configure the screen title?
    readonly configuresScreenTitle?: boolean; // defaults to `false`

    // ##getActionTables:
    // TODO: This seems to duplicate `getIndirectTable` in the action
    // descriptor, or potentially clash with it.  If we run into trouble with
    // either of them we should consolidate.
    getActionTables?(tables: InputOutputTables, desc: T, schema: SchemaInspector): InputOutputTables | undefined;
}

export interface SchemaInspector {
    readonly schema: TypeSchema;
    readonly userProfileTableInfo: UserProfileTableInfo | undefined;

    findTable(ref: TableName | TableRefGlideType | undefined): TableGlideType | undefined;
}

export interface AppDescriptionContext extends SchemaInspector {
    readonly appID: string;
    readonly appKind: AppKind;
    readonly appDescription: AppDescription | undefined;
    readonly builderActions: BuilderActionsForApp;
    readonly userFeatures: UserFeatures;
    readonly eminenceFlags: EminenceFlags;
    getBuilderAction(id: string): BuilderAction | undefined;
}

export function isNativeTableInAppDescriptionContext(adc: AppDescriptionContext, tableName: TableName): boolean {
    return definedMap(adc.appDescription, a => isNativeTable(getSourceMetadata(a), tableName)) ?? false;
}

export function getAppKindFromAppDescriptionContext(adc: AppDescriptionContext): AppKind {
    return definedMap(adc.appDescription, getAppKind) ?? defaultAppKind;
}

export interface ExistingAppDescriptionContext extends AppDescriptionContext {
    readonly appDescription: AppDescription;
}

export interface RewritingComponentConfiguratorContext extends AppDescriptionContext {
    // Returns whether the screen was added successfully/is available
    requireScreen(screenName: string): boolean;
    requireMenuScreen(menuID: string): boolean;
}

export function makeSimpleSchemaInspector(
    schema: TypeSchema,
    userProfileTableInfo: UserProfileTableInfo | undefined
): SchemaInspector {
    return { schema, userProfileTableInfo, findTable: r => findTable(schema, r) };
}

function makeSimpleAppDescriptionContextUnmemoized(
    appID: string,
    appKind: AppKind,
    appDescription: AppDescription,
    builderActions: BuilderActionsForApp | undefined,
    schema: TypeSchema,
    userFeatures: UserFeatures,
    eminenceFlags: EminenceFlags
): ExistingAppDescriptionContext;
function makeSimpleAppDescriptionContextUnmemoized(
    appID: string,
    appKind: AppKind,
    appDescription: AppDescription | undefined,
    builderActions: BuilderActionsForApp | undefined,
    schema: TypeSchema,
    userFeatures: UserFeatures,
    eminenceFlags: EminenceFlags
): AppDescriptionContext;
function makeSimpleAppDescriptionContextUnmemoized(
    appID: string,
    appKind: AppKind,
    appDescription: AppDescription | undefined,
    builderActions: BuilderActionsForApp | undefined,
    schema: TypeSchema,
    userFeatures: UserFeatures,
    eminenceFlags: EminenceFlags
): AppDescriptionContext {
    return {
        ...makeSimpleSchemaInspector(schema, definedMap(appDescription, getUserProfileTableInfo)),
        appID,
        appKind,
        appDescription,
        builderActions: builderActions ?? new Map(),
        userFeatures,
        eminenceFlags,
        getBuilderAction: id => builderActions?.get(id),
    };
}

export const makeSimpleAppDescriptionContext = memoizeFunction(
    "makeSimpleAppDescriptionContext",
    makeSimpleAppDescriptionContextUnmemoized
);

export function elevateAppDescriptionContext<T extends AppDescriptionContext>(
    adc: T
): (T & ExistingAppDescriptionContext) | undefined {
    if (adc.appDescription === undefined) return undefined;
    return adc as T & ExistingAppDescriptionContext;
}

export function makeSimpleComponentConfiguratorContext(
    appID: string,
    appDescription: AppDescription,
    builderActions: BuilderActionsForApp,
    schema: TypeSchema,
    userFeatures: UserFeatures,
    eminenceFlags: EminenceFlags
): RewritingComponentConfiguratorContext {
    return {
        ...makeSimpleAppDescriptionContext(
            appID,
            getAppKind(appDescription),
            appDescription,
            builderActions,
            schema,
            userFeatures,
            eminenceFlags
        ),
        appKind: getAppKind(appDescription),
        getBuilderAction: id => builderActions.get(id),
        requireScreen: n => appDescription.screenDescriptions[n] !== undefined,
        requireMenuScreen: () => true,
    };
}

export interface PasteboardState {
    readonly components: readonly ComponentDescription[];
    readonly appKind: AppKind;
}

export interface InteractiveComponentConfiguratorContext extends RewritingComponentConfiguratorContext, AppUpdater {
    readonly appDescription: AppDescription;
    readonly user: UserData | undefined;
    readonly owner: Owner | undefined;
    readonly pasteboard: PasteboardState;

    addFormScreen(screensCreated: Set<string>, makeScreen: () => FormScreenDescription): string;
    addFreeScreen(screensCreated: Set<string>, makeScreen: () => ClassOrArrayScreenDescription): string;
    addFreeScreenFromTable(table: TableGlideType): string;

    saveBuilderAction(
        actionID: string | undefined,
        tableName: TableName,
        title: string | undefined,
        description: string | undefined,
        action: CompoundActionNode
    ): Promise<string>;
    makeDefaultCompoundAction(
        table: TableGlideType,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): Promise<string>;

    isComponentNew<T extends ComponentDescription>(
        descriptor: ComponentDescriptor<T> | ComponentSpecialCaseDescriptor
    ): boolean;

    upgradeAppToPro(source: string, setProAsDefault: boolean): void;

    pushUndoState(): void;
}

export interface ComponentErrorAndLink {
    readonly errorMessage: string;
    readonly linkMessage: string;
    readonly linkURL: string;
}

export interface ComponentSpecialCaseDescriptor {
    readonly name: string;
    readonly analyticsName: string;
    readonly description: string;
    readonly img: string;
    readonly group: string;
    readonly isNew?: boolean;
    readonly isPro?: boolean;
    readonly needsInputContext?: boolean;
    readonly isLegacy?: boolean;
    readonly appKinds: AppKind | "both";
}

export enum ComponentPrerequisite {
    StripeConnect,
}

export interface ActionsRecord {
    readonly actions: readonly ActionDescription[];
    readonly [name: string]: readonly ActionDescription[];
}

export function mapActionsRecord(
    actions: ActionsRecord,
    f: (a: readonly ActionDescription[]) => readonly ActionDescription[]
): ActionsRecord {
    return mapRecord(actions, f) as ActionsRecord;
}

// [column name, edits in screen context (vs in indirect table), is Add Row, table name]
export type EditedColumn = readonly [
    columnName: string,
    editsInScreenContext: boolean,
    isAddRow: boolean,
    tableName: TableName
];
export type EditedColumns = readonly EditedColumn[];

export interface EditedColumnsAndTables {
    readonly editedColumns: EditedColumns;
    readonly deletedTables: readonly TableName[];
}

export const emptyEditedColumnsAndTables: EditedColumnsAndTables = { editedColumns: [], deletedTables: [] };

export function combineEditedColumnsAndTables(
    e1: EditedColumnsAndTables,
    e2: EditedColumnsAndTables | undefined
): EditedColumnsAndTables {
    if (e2 === undefined) return e1;
    return {
        editedColumns: [...e1.editedColumns, ...e2.editedColumns],
        deletedTables: [...e1.deletedTables, ...e2.deletedTables],
    };
}

export enum PageScreenTarget {
    Current = "current",
    Main = "main",
    SmallModal = "small-modal",
    LargeModal = "large-modal",
}

// These must all easily be implementable over a wire protocol.  In particular
// they can't return values, and they're not expected to execute
// synchronously.
export interface WireActionCallbacks {
    openLink(url: string): void;
    showToast(success: boolean, message: string): void;
    copyToClipboard(data: string): void;
    playSound(sound: Sound): void;
    signOut(): void;
}

export interface WireValueChangedBackend {
    valueChanged(token: string, value: PrimitiveValue): void;
}

export interface WireActionBackend extends WireValueChangedBackend {
    readonly actionCallbacks: WireActionCallbacks;

    pushFreeScreen(screenName: string, target: PageScreenTarget, row: Row | undefined): void;
    pushDefaultClassScreen(tableName: TableName, row: Row, target: PageScreenTarget): void;
    pushDefaultEditScreen(tableName: TableName, row: Row, target: PageScreenTarget): void;
    pushFormScreen(screenName: string, row: Row | undefined, target: PageScreenTarget): void;

    closeScreen(position: PageScreenPosition.Modal): void;

    navigateToTab(tabScreenName: string): boolean;
    navigateToPath(path: ParsedPath): boolean;
    navigateToUserProfile(): boolean;

    addRow(tableName: TableName, row: Row): Promise<boolean>;
    setColumnsInRow(tableName: TableName, row: Row, updates: Record<string, GroundValue>): Promise<boolean>;
    deleteRow(tableName: TableName, row: Row): Promise<boolean>;

    saveEditScreenChanges(tableName: TableName, screenContext: InternalScreenContext): Promise<boolean>;

    signIn(): void;
}

export interface InternalScreenContext {
    // This is always a visible row
    readonly inputRow: Row | undefined;
    // This row, if present, is always invisible, and has to be deleted from
    // the table when the screen is popped.  All edit screens will have this
    // set. For regular detail screens this is not set, and the input row also
    // acts as the output row.  Unfortunately this is wrong because it makes
    // us guess whether we have the ##correctOutputRow if we have to fall back
    // to the input row.
    readonly outputRow: Row | undefined;
}

export interface InternalRowContext extends InternalScreenContext {
    // This is always a visible row
    readonly containingScreenRow: Row | undefined;
}

export type WireActionHydrator = (
    hb: WireRowActionHydrationValueProvider
) => WireActionRunner | [runner: WireActionRunner, url: string | undefined] | undefined;
export type WireActionRunner = (b: WireActionBackend, handledByFrontend: boolean) => Promise<boolean>;
export type WireComponentWithFlags =
    | WireComponent
    // ##isRequiredVsIsValid:
    // TODO: Can't we have `isValid` instead of `isRequired`?  There are other
    // ways of being invalid than not having a value.  We'd still need
    // `hasValue`, because edit/form screens require at least one entry with a
    // value.  A required component that doesn't have a value would have to
    // set `isValid` to `false`, still.
    | readonly [component: WireComponent, isRequired: boolean, hasValue: boolean];
export type WireRowComponentHydrator = (hb: WireRowComponentHydrationBackend) => WireComponentWithFlags | undefined;
// For array screens the `rhb` will be `undefined`
export type WireTableComponentHydrator = (
    thb: WireTableComponentHydrationBackend,
    rhb: WireRowComponentHydrationBackend | undefined
) => WireComponentWithFlags | undefined;
export type WireScreenHydrator = (
    defaultTitle: string | undefined,
    searchBarValue: string,
    backButtonAction: WireAction | undefined,
    searchBarOnChangeToken: string,
    hb: WireTableComponentHydrationBackend
) => WireScreen | undefined;

export type WireValueGetter = (hb: WireRowHydrationValueProvider) => GroundValue | null;
export type WireRowComponentValueGetter = (hb: WireRowComponentHydrationBackend) => GroundValue | null;
export type WirePredicate = (hb: WireRowHydrationValueProvider) => boolean;

export interface ContextTableTypes extends InputOutputTables {
    readonly containingScreen: TableGlideType | undefined;
}

export interface WireInflationBackend {
    readonly adc: ExistingAppDescriptionContext;
    readonly appFacilities: AppFacilities;
    readonly tables: ContextTableTypes;
    readonly mutatingScreenKind: MutatingScreenKind | undefined;

    getValueGetterForColumn(
        sourceColumn: SourceColumn,
        inOutputRow: boolean,
        withFormat: boolean
    ): readonly [WireValueGetter, ColumnType];
    getValueGetterForProperty(
        desc: LegacyPropertyDescription | undefined,
        withFormat: boolean,
        opts?: {
            inOutputRow?: boolean; // default: false
            columnFirst?: boolean; // default: true
        }
    ): readonly [WireValueGetter, ColumnType];

    inflatePredicate(predicate: Formula, inOutputRow: boolean): WirePredicate | undefined;
    inflateFilters(
        filters: readonly FilterArrayTransform[],
        inOutputRow: boolean,
        tableTypeOverride: TableGlideType | undefined
    ): WirePredicate;

    makeInflationBackendForTables(tables: ContextTableTypes): WireRowComponentInflationBackend;
}

export type WireTableGetter = (hb: WireRowComponentHydrationBackend) => WireTableComponentHydrationBackend | undefined;

export interface WireRowComponentInflationBackend extends WireInflationBackend {
    getValueGetterForTableComponent(
        desc: LegacyPropertyDescription,
        transforms: readonly ArrayTransform[]
    ): [WireTableGetter, WireInflationBackend] | undefined;
}

export interface WireHydrationBackend {
    registerAction(run: WireActionRunner): string;
    getParsedPath(): ParsedPath;
}

interface WireHydrationValueProvider {}

export interface WireRowHydrationValueProvider extends WireHydrationValueProvider {
    // FIXME: This need to be behind accessors, too, to enable subscribing.
    // Even checking against `undefined` must include some kind of
    // subscription.
    readonly rowContext: InternalRowContext | undefined;

    // FIXME: These are just used internally in the backend and shouldn't be
    // here, probably.
    getGlobalValue(tableName: TableName | undefined, rootPath: RootPath): GroundValue;
    getColumnInRow(tableName: TableName, rootPath: RootPath, row: Row, columnPath: RelativePath): GroundValue;
}

export interface WireRowActionHydrationValueProvider extends WireRowHydrationValueProvider {
    getIsUserSignedIn(): boolean;
    getIsTabVisible(tabScreenName: string): boolean;
    getIsModalVisible(): boolean;
}

export interface WireRowValueProviderMaker {
    makeRowValueProvider(row: Row): WireRowHydrationValueProvider;
}

export interface WireTableTransformValueProvider extends WireRowValueProviderMaker {
    makeHydrationBackendForTable(rows: Row[]): WireTableComponentHydrationBackend;
}

export interface WireAlwaysEditableValue<T> extends WireEditableValue<T> {
    readonly onChangeToken: string;
}

export interface WireHydratedSubComponents {
    readonly components: readonly (WireComponent | null)[];
    readonly allValid: boolean;
    readonly isRequired: boolean;
    readonly haveValues: boolean;
    readonly componentsRowContext: InternalRowContext | undefined;
}

export interface WireRowComponentHydrationBackend extends WireHydrationBackend, WireRowActionHydrationValueProvider {
    readonly screenPosition: PageScreenPosition;
    readonly currentScreenTitle: string | undefined;

    // FIXME: This should take a table, because we can change values outside
    // the context row, but right now that's not supported.
    registerOnValueChange(columnName: string): string | undefined;

    makeTableTransformValueProvider(tableType: TableGlideType): WireTableTransformValueProvider | undefined;

    // `convert`, if present, converts the value coming in from the frontend
    // to whatever the backend actually wants to store.
    getState<T>(
        name: string,
        validate: (v: unknown) => v is T,
        defaultValue: T,
        convert?: (v: unknown) => T
    ): WireAlwaysEditableValue<T>;

    makeHydrationBackendForRow(
        row: Row | undefined,
        tables: InputOutputTables | undefined
    ): WireRowComponentHydrationBackend;
    makeHydrationBackendForOutputRow(): WireRowComponentHydrationBackend | undefined;

    getScreenTitle(screen: ParsedScreen): string | null | undefined;

    // May only be called at most once per component.  If `outputTable` is
    // defined, sub-components will be hydrated with an invisible output row
    // from that table.  We use that for form containers.
    hydrateSubComponents(
        hydrators: readonly WireComponentHydratorWithID[],
        outputTable: TableGlideType | undefined
    ): WireHydratedSubComponents;
}

export interface WireTableComponentHydrationBackend extends WireHydrationBackend, WireHydrationValueProvider {
    // FIXME: This need to be behind accessors, too, to enable subscribing.
    // Even checking against `undefined` must include some kind of
    // subscription.
    readonly tableScreenContext: readonly Row[];

    makeHydrationBackendForRow(row: Row): WireRowComponentHydrationBackend;

    withRows(rows: readonly Row[]): WireTableComponentHydrationBackend;
}

export type WireComponentHydratorWithID = [hydrator: WireRowComponentHydrator, componentID: string | undefined];

export interface ComponentHandler<T extends Description> {
    readonly kind: ComponentKind;
    // Can this component be added if there are no columns available to bind?
    readonly needsColumns: boolean;
    readonly isNonEmptyValidationStopper: boolean;
    readonly prerequisites: readonly ComponentPrerequisite[];
    // This means the component is configured by Glide and the user shouldn't
    // be able to configure it, delete, duplicate, cut/copy, ...  We use it
    // for the User Profile component.
    readonly isGlideManaged: boolean;
    readonly appKinds: AppKind | "both";

    getDescriptiveName(desc: T, tables: InputOutputTables, ccc: AppDescriptionContext): [string, string];
    getDescriptor(
        desc: T | undefined,
        table: TableGlideType | undefined,
        ccc: AppDescriptionContext,
        mutatingScreenKind: MutatingScreenKind | undefined,
        screenContext?: ValueProducer,
        appEnvironment?: AppEnvironment
    ): ComponentDescriptor<T>;
    getPropertyAndActionDescriptors(
        desc: T | undefined,
        table: TableGlideType | undefined,
        ccc: AppDescriptionContext,
        mutatingScreenKind: MutatingScreenKind | undefined,
        screenContext?: ValueProducer,
        appEnvironment?: MinimalAppEnvironment
    ): readonly PropertyDescriptor[];
    getCaption(desc: T, table: TableGlideType | undefined, ccc: AppDescriptionContext): string | undefined;
    getIsEditor(
        desc: T | undefined,
        ccc: AppDescriptionContext,
        specialCaseDescriptors: ComponentSpecialCaseDescriptor | undefined
    ): boolean;
    // Returns [tables, is direct]
    getTablesForActions(
        tables: InputOutputTables,
        desc: T,
        schema: SchemaInspector
    ): [InputOutputTables, boolean] | undefined;
    getActions(
        desc: T,
        table: TableGlideType | undefined,
        schema: SchemaInspector | undefined,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ActionsRecord;
    // Must return `undefined` if the component does not support
    // subcomponents, and it must return an array if it does.
    getSubComponents(desc: T): readonly ComponentDescription[] | undefined;
    // Should return `undefined` only if the component doesn't support
    // subcomponents or if there's an error, in particular if a table doesn't
    // exist.
    //
    // ##subComponentMutatingScreenKind:
    // When it returns a mutating screen kind, then the columns
    // edited in the sub-components are not in the context of the container,
    // and vice versa.
    getSubComponentTables(
        desc: T,
        containingTables: InputOutputTables,
        schema: SchemaInspector
    ): [InputOutputTables, MutatingScreenKind | undefined] | undefined;

    getSpecialCaseDescriptors(ccc: AppDescriptionContext): readonly ComponentSpecialCaseDescriptor[];

    // These two returns only columns used from the context, not columns used
    // from the user profile row, for example.
    getColumnsUsed(desc: T, tables: InputOutputTables, ccc: AppDescriptionContext): ReadonlyArray<TableColumn>;
    getSearchableColumnsUsed(
        desc: T,
        tables: InputOutputTables,
        ccc: AppDescriptionContext
    ): ReadonlyArray<TableColumn>;

    // Does not include screens used by actions.
    getScreensUsed(desc: T, schema: SchemaInspector, tables: InputOutputTables | undefined): readonly string[];

    // ##getEditedColumns:
    // Returns [columnName, editsInScreenContext, tableName]
    // `editsInScreenContext` is `false` in cases where the component edits
    // a column that doesn't affect the current screen's context.  For example,
    // an edit screen might have an inline Checklist which edits some other
    // table.
    getEditedColumns(
        desc: T,
        tables: InputOutputTables,
        ccc: AppDescriptionContext,
        mutatingScreenKind: MutatingScreenKind | undefined,
        withActions: boolean
    ): EditedColumnsAndTables;
    getDefaultValues(
        builder: ComponentBuilder,
        desc: T,
        tables: InputOutputTables
    ): readonly [string, SerializedComponent][];
    // FIXME: What does this do what ##getEditedColumns doesn't already do?
    // FIXME: Use `EditedColumnsAndTables` here, too, and we can get rid of
    // `AllowedDataEdits`.
    getAdditionalDataEdits(
        desc: T,
        tables: InputOutputTables,
        ccc: AppDescriptionContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): AllowedDataEdits | undefined;

    makeNeedsSearch(builder: ComponentBuilder, desc: T, tables: InputOutputTables): SerializedComponent | undefined;
    // The side panel needs to determine whether the containing screen is searchable
    // based on whether this is possibly searchable. Unfortunately, the side panel
    // doesn't have any SerializedElementBuilders, ComponentBuilders, or InputOutputTables
    // to work with, so we expose this separately.
    //
    // If you implement needsSearch, you need to implement this, too.
    isSearchable(desc: T): boolean;
    needValidation(desc: T): boolean;
    makeValidationFlag(
        b: SerializedElementBuilder,
        desc: T,
        outputContext: SerializedComponent,
        table: TableGlideType
    ): SerializedComponent | undefined;
    entryForTableColumn?(
        builder: ComponentBuilder,
        desc: T,
        tables: InputOutputTables,
        mutatingScreenKind: MutatingScreenKind | undefined,
        validFlag: SerializedComponent | undefined,
        outputContext: SerializedComponent,
        searchVariable: SerializedComponent | undefined,
        searchNotActive: SerializedComponent | undefined,
        uniqueID: string
    ): SerializedComponent;
    getQuotaBannerRequirements(desc: T): QuotaBannerRequirements;
    rewriteAfterReload(
        desc: T,
        tables: InputOutputTables,
        ccc: RewritingComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined,
        isRewrite: boolean
    ): T | undefined;
    newComponent(
        tables: InputOutputTables,
        usedProperties: ReadonlySet<TableColumn>,
        editedColumns: ReadonlySet<TableColumn>,
        ccc: InteractiveComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): T | undefined;
    newSpecialCaseComponent(
        specialCaseDescriptor: ComponentSpecialCaseDescriptor,
        tables: InputOutputTables,
        usedColumns: ReadonlySet<TableColumn>,
        editedColumns: ReadonlySet<TableColumn>,
        iccc: InteractiveComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): T | undefined;
    updateComponent(
        desc: T,
        updates: Partial<T>,
        tables: InputOutputTables,
        ccc: RewritingComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): T;
    duplicateComponent(
        desc: T,
        copyTables: InputOutputTables | undefined,
        iccc: InteractiveComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined,
        screensCreated: Set<string>
    ): Promise<T>;
    getErrorAndLink(_desc: T, _table: TableGlideType): ComponentErrorAndLink | undefined;

    inflate?(ib: WireRowComponentInflationBackend, desc: T): WireRowComponentHydrator | undefined;
}

export function componentErrorAndLinkForReference(table: TableGlideType): ComponentErrorAndLink {
    return {
        errorMessage: `The "${sheetNameForTable(table)}" sheet doesn't have a relation column.`,
        linkMessage: "Learn how to add one",
        linkURL: getDocURL("addRelationColumn"),
    };
}
